import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { LandingScreen } from '../components/Interior/LandingScreen';
import { DesignQuestionScreen } from '../components/Interior/DesignQuestionScreen';
import { EmailScreen } from '../components/Interior/EmailScreen';
import { UserIdScreen } from '../components/Interior/UserIdScreen';
import { LoadingScreen } from '../components/Interior/LoadingScreen';
import { PressAnyKeyScreen } from '../components/Interior/PressAnyKeyScreen';
import { PreferenceScreen } from '../components/Interior/PreferenceScreen';
import { ModelSelectionScreen } from '../components/Interior/ModelSelectionScreen';
import { MagicLinkSentScreen } from '../components/Interior/MagicLinkSentScreen';
import { OtpEntryScreen } from '../components/Interior/OtpEntryScreen';
import { PinRevealScreen } from '../components/Interior/PinRevealScreen';
import { interiorModels } from '../data/interiorMockData';
import { ThemeProvider } from '../components/styles/ThemeContext';
import { FlowSwitcher, defaultFlows } from '../components/FlowSwitcher';
type InteriorScreen = 'landing' | 'question' | 'email' | 'userId' | 'magicLinkSent' | 'otpEntry' | 'pinReveal' | 'models' | 'loading' | 'pressKey' | 'preference';
const FLOW_IDS = ['current', 'magiclink', 'otp', 'pin'] as const;
type FlowId = (typeof FLOW_IDS)[number];
interface FlowState {
  currentScreen: InteriorScreen;
  userEmail: string | null;
  userId: string | null;
  selectedModelId: string | null;
  fromEmail: boolean;
}
const initialFlowState: FlowState = {
  currentScreen: 'landing',
  userEmail: null,
  userId: null,
  selectedModelId: null,
  fromEmail: false
};
// Map screen names to URL path segments
const screenToPath: Record<InteriorScreen, string> = {
  landing: '',
  question: 'question',
  email: 'email',
  userId: 'login',
  magicLinkSent: 'sent',
  otpEntry: 'verify',
  pinReveal: 'reveal',
  models: 'models',
  loading: 'loading',
  pressKey: 'ready',
  preference: 'preference'
};
// Map URL path segments back to screen names
const pathToScreen: Record<string, InteriorScreen> = {
  '': 'landing',
  question: 'question',
  email: 'email',
  login: 'userId',
  sent: 'magicLinkSent',
  verify: 'otpEntry',
  reveal: 'pinReveal',
  models: 'models',
  loading: 'loading',
  ready: 'pressKey',
  preference: 'preference'
};
function buildUrl(flowId: string, screen: InteriorScreen): string {
  const segment = screenToPath[screen];
  if (segment === '') return `/${flowId}`;
  return `/${flowId}/${segment}`;
}
function parseUrl(pathname: string): {
  flowId: FlowId;
  screen: InteriorScreen;
} {
  // Remove leading slash and split
  const parts = pathname.replace(/^\//, '').split('/');
  const maybeFlowId = parts[0] || '';
  const screenSegment = parts[1] || '';
  // Check if the first segment is a valid flow ID
  if (FLOW_IDS.includes(maybeFlowId as FlowId)) {
    const screen = pathToScreen[screenSegment] || 'landing';
    return {
      flowId: maybeFlowId as FlowId,
      screen
    };
  }
  // Fallback: default to 'current' flow landing
  return {
    flowId: 'current',
    screen: 'landing'
  };
}
function InteriorAppContent() {
  // Parse initial URL
  const initial = parseUrl(window.location.pathname);
  const [activeFlowId, setActiveFlowId] = useState<string>(initial.flowId);
  const [flowStates, setFlowStates] = useState<Record<string, FlowState>>(() => {
    const states: Record<string, FlowState> = {};
    for (const id of FLOW_IDS) {
      states[id] = {
        ...initialFlowState,
        // Set the initial screen for the flow that matches the URL
        currentScreen: id === initial.flowId ? initial.screen : 'landing'
      };
    }
    return states;
  });
  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);
  const currentFlowState = flowStates[activeFlowId];
  const {
    currentScreen,
    fromEmail,
    userEmail
  } = currentFlowState;
  const updateFlowState = useCallback((updates: Partial<FlowState>) => {
    setFlowStates(prev => ({
      ...prev,
      [activeFlowId]: {
        ...prev[activeFlowId],
        ...updates
      }
    }));
  }, [activeFlowId]);
  // Helper to navigate: update state + push URL
  const navigateTo = useCallback((screen: InteriorScreen, extraState?: Partial<FlowState>) => {
    const updates = {
      currentScreen: screen,
      ...extraState
    };
    setFlowStates(prev => ({
      ...prev,
      [activeFlowId]: {
        ...prev[activeFlowId],
        ...updates
      }
    }));
    window.history.pushState({}, '', buildUrl(activeFlowId, screen));
  }, [activeFlowId]);
  // When switching flows, update URL to that flow's current screen
  const handleSelectFlow = useCallback((newFlowId: string) => {
    setActiveFlowId(newFlowId);
    const targetScreen = flowStates[newFlowId]?.currentScreen || 'landing';
    window.history.pushState({}, '', buildUrl(newFlowId, targetScreen));
  }, [flowStates]);
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      const keyMap: Record<string, string> = {
        '1': 'current',
        '2': 'magiclink',
        '3': 'otp',
        '4': 'pin'
      };
      const flowId = keyMap[e.key];
      if (flowId) {
        handleSelectFlow(flowId);
        setIsSwitcherOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSelectFlow]);
  // Handle browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const {
        flowId,
        screen
      } = parseUrl(window.location.pathname);
      setActiveFlowId(flowId);
      setFlowStates(prev => ({
        ...prev,
        [flowId]: {
          ...prev[flowId],
          currentScreen: screen
        }
      }));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  // Navigation handlers
  const handleBeginTour = () => {
    if (activeFlowId === 'current') {
      navigateTo('question');
    } else {
      navigateTo('email');
    }
  };
  const handleDesignYes = () => {
    navigateTo('userId', {
      fromEmail: false
    });
  };
  const handleDesignNo = () => {
    navigateTo('email', {
      fromEmail: true
    });
  };
  const handleEmailSubmit = (email: string) => {
    if (activeFlowId === 'current') {
      navigateTo('userId', {
        userEmail: email
      });
    } else if (activeFlowId === 'magiclink') {
      navigateTo('magicLinkSent', {
        userEmail: email
      });
    } else if (activeFlowId === 'otp') {
      navigateTo('otpEntry', {
        userEmail: email
      });
    } else if (activeFlowId === 'pin') {
      navigateTo('pinReveal', {
        userEmail: email
      });
    }
  };
  const handleUserIdSubmit = (id: string) => {
    navigateTo('models', {
      userId: id
    });
  };
  const handleMagicLinkContinue = () => {
    navigateTo('models');
  };
  const handleOtpSubmit = (_code: string) => {
    navigateTo('models');
  };
  const handlePinContinue = () => {
    navigateTo('models');
  };
  const handleSkipAuth = () => {
    navigateTo('models');
  };
  const handleModelSelect = (modelId: string) => {
    navigateTo('loading', {
      selectedModelId: modelId
    });
  };
  const handleLoadingComplete = () => {
    navigateTo('pressKey');
  };
  const handlePressKey = () => {
    navigateTo('preference');
  };
  const handleSelectGuidedTour = () => {
    window.open('https://app-visualizer.resvr.com?mode=guided', '_blank');
  };
  const handleSelectMouseKeyboard = () => {
    window.open('https://app-visualizer.resvr.com?mode=manual', '_blank');
  };
  const handleBack = () => {
    if (currentScreen === 'question') {
      navigateTo('landing');
    } else if (currentScreen === 'email') {
      if (activeFlowId === 'current') {
        navigateTo('question');
      } else {
        navigateTo('landing');
      }
    } else if (currentScreen === 'userId') {
      if (fromEmail) {
        navigateTo('email');
      } else {
        navigateTo('question');
      }
    } else if (currentScreen === 'magicLinkSent' || currentScreen === 'otpEntry' || currentScreen === 'pinReveal') {
      navigateTo('email');
    } else if (currentScreen === 'models') {
      if (activeFlowId === 'current') {
        navigateTo('userId');
      } else if (activeFlowId === 'magiclink') {
        navigateTo('magicLinkSent');
      } else if (activeFlowId === 'otp') {
        navigateTo('otpEntry');
      } else if (activeFlowId === 'pin') {
        navigateTo('pinReveal');
      }
    }
  };
  const handleExit = () => {
    window.history.pushState({}, '', '/');
    window.location.href = '/';
  };
  return <div className="relative w-full h-screen overflow-hidden" data-id="element-349">
      <AnimatePresence mode="wait" data-id="element-350">
        {currentScreen === 'landing' && <motion.div key={`${activeFlowId}-landing`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-351">
            <LandingScreen onBeginTour={handleBeginTour} onExit={handleExit} data-id="element-352" />
          </motion.div>}

        {currentScreen === 'question' && <motion.div key={`${activeFlowId}-question`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-353">
            <DesignQuestionScreen onYes={handleDesignYes} onNo={handleDesignNo} onBack={handleBack} onSkip={handleSkipAuth} data-id="element-354" />
          </motion.div>}

        {currentScreen === 'email' && <motion.div key={`${activeFlowId}-email`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-355">
            <EmailScreen onSubmit={handleEmailSubmit} onBack={handleBack} onSkip={handleSkipAuth} data-id="element-356" />
          </motion.div>}

        {currentScreen === 'userId' && <motion.div key={`${activeFlowId}-userId`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-357">
            <UserIdScreen onSubmit={handleUserIdSubmit} onBack={handleBack} fromEmail={fromEmail} onSkip={handleSkipAuth} data-id="element-358" />
          </motion.div>}

        {currentScreen === 'magicLinkSent' && <motion.div key={`${activeFlowId}-magicLinkSent`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-359">
            <MagicLinkSentScreen email={userEmail || ''} onContinue={handleMagicLinkContinue} onBack={handleBack} onSkip={handleSkipAuth} data-id="element-360" />
          </motion.div>}

        {currentScreen === 'otpEntry' && <motion.div key={`${activeFlowId}-otpEntry`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-361">
            <OtpEntryScreen email={userEmail || ''} onSubmit={handleOtpSubmit} onBack={handleBack} onSkip={handleSkipAuth} data-id="element-362" />
          </motion.div>}

        {currentScreen === 'pinReveal' && <motion.div key={`${activeFlowId}-pinReveal`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-363">
            <PinRevealScreen email={userEmail || ''} onContinue={handlePinContinue} onBack={handleBack} onSkip={handleSkipAuth} data-id="element-364" />
          </motion.div>}

        {currentScreen === 'models' && <motion.div key={`${activeFlowId}-models`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-365">
            <ModelSelectionScreen models={interiorModels} onSelectModel={handleModelSelect} onBack={handleBack} data-id="element-366" />
          </motion.div>}

        {currentScreen === 'loading' && <motion.div key={`${activeFlowId}-loading`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-367">
            <LoadingScreen onComplete={handleLoadingComplete} data-id="element-368" />
          </motion.div>}

        {currentScreen === 'pressKey' && <motion.div key={`${activeFlowId}-pressKey`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-369">
            <PressAnyKeyScreen onContinue={handlePressKey} data-id="element-370" />
          </motion.div>}

        {currentScreen === 'preference' && <motion.div key={`${activeFlowId}-preference`} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-371">
            <PreferenceScreen onSelectGuidedTour={handleSelectGuidedTour} onSelectMouseKeyboard={handleSelectMouseKeyboard} data-id="element-372" />
          </motion.div>}
      </AnimatePresence>

      {/* Flow Switcher */}
      <FlowSwitcher flows={defaultFlows} activeFlowId={activeFlowId} onSelectFlow={handleSelectFlow} isOpen={isSwitcherOpen} onToggle={() => setIsSwitcherOpen(!isSwitcherOpen)} onClose={() => setIsSwitcherOpen(false)} data-id="element-373" />
    </div>;
}
export function InteriorApp() {
  return <ThemeProvider data-id="element-374">
      <InteriorAppContent data-id="element-375" />
    </ThemeProvider>;
}